import React from 'react';
import { AlertTriangle, CheckCircle2, DollarSign, Clock } from 'lucide-react';
import { Badge } from './ui/badge';

const REASON_LABELS = {
  hours_not_calculated:   'Hours not computed',
  missing_pay_rate:       'No pay rate',
  hours_not_approved:     'Hours not approved',
  stripe_not_onboarded:   'Stripe onboarding incomplete',
  admin_blocked:          'Admin-blocked',
  at_risk:                'At-risk (missed day-confirm)',
  rate_outside_range:     'Rate outside range (no override)',
  clock_duration_anomaly: 'Clock duration anomaly (>14h)',
  clockout_hygiene:       'Clock duration > shift × 1.5 — admin review',
};

/**
 * NotPayableBadge — visual safeguard surfaced on every completed booking
 * row so admins never confuse a half-finished booking for "ready to pay".
 *
 * Inputs (passed from the admin shift-detail booking row):
 *   payable_status   : 'ready' | 'needs_review' | 'paid' | 'not_completed'
 *   payable_blockers : ['hours_not_calculated', 'missing_pay_rate', ...]
 */
const NotPayableBadge = ({
  payable_status,
  payable_blockers = [],
  dataTestId,
}) => {
  if (payable_status === 'not_completed' || !payable_status) {
    // Pre-shift / not yet clocked out — no badge needed
    return null;
  }
  if (payable_status === 'paid') {
    return (
      <Badge
        className="bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1"
        data-testid={dataTestId || 'payable-paid-badge'}
      >
        <CheckCircle2 className="w-3 h-3" /> Paid
      </Badge>
    );
  }
  if (payable_status === 'ready') {
    return (
      <Badge
        className="bg-blue-100 text-blue-800 border border-blue-300 inline-flex items-center gap-1"
        data-testid={dataTestId || 'payable-ready-badge'}
      >
        <DollarSign className="w-3 h-3" /> Ready to Pay
      </Badge>
    );
  }
  // payable_status === 'needs_review'
  const labels = (payable_blockers || [])
    .map((b) => REASON_LABELS[b] || b)
    .filter(Boolean);
  const tooltip = labels.length
    ? `Not Payable — ${labels.join(' · ')}`
    : 'Not Payable — admin review required';
  return (
    <div className="inline-flex flex-col items-start gap-0.5" data-testid={dataTestId || 'payable-needs-review-badge'}>
      <Badge
        className="bg-amber-100 text-amber-900 border border-amber-400 inline-flex items-center gap-1"
        title={tooltip}
      >
        <AlertTriangle className="w-3 h-3" /> Not Payable / Needs Review
      </Badge>
      {labels.length > 0 && (
        <div className="text-[10px] text-amber-800 leading-tight max-w-[260px]">
          {labels.slice(0, 3).join(' · ')}
          {labels.length > 3 && ` · +${labels.length - 3} more`}
        </div>
      )}
    </div>
  );
};

export default NotPayableBadge;
